import React, {useState, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  Tooltip,
  LinearProgress,
  FormControlLabel,
  Checkbox,
  Skeleton,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { httpRequest } from '../../@crema/services/auth/jwt-auth';
import { formatDateToJerusalem } from '../../utils/dateFormatter';

const AllCredentials = () => {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCredential, setEditingCredential] = useState(null);
  const [formData, setFormData] = useState({
    credential_name: '',
    username: '',
    password: '',
    metadata: {},
    is_system_file_upload: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showApiKey, setShowApiKey] = useState({});

  // Integration sources removed - no longer needed

  useEffect(() => {
    fetchCredentials();
  }, []);

  const fetchCredentials = async () => {
    try {
      setLoading(true);
      const response = await httpRequest('api-credentials/stats', { method: 'GET' });
      console.log('API Credentials Data:', response.data.data);
      setCredentials(response.data.data || []);
    } catch (err) {
      console.error('Error fetching credentials:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingCredential(null);
    setFormData({
      credential_name: '',
      username: '',
      password: '',
      metadata: {},
    });
    setOpenDialog(true);
  };

  const handleSave = async () => {
    try {
      const url = editingCredential
        ? `api-credentials/${editingCredential.id}`
        : 'api-credentials';

      const method = editingCredential ? 'PUT' : 'POST';

      await httpRequest(url, {
        method: method,
        body: formData,
      });

      setOpenDialog(false);
      fetchCredentials();
    } catch (err) {
      console.error('Error saving credential:', err);
    }
  };

  const toggleApiKeyVisibility = (id) => {
    setShowApiKey((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const formatApiKey = (apiKey) => {
    if (!apiKey) return '';
    return apiKey.length > 20
      ? `${apiKey.substring(0, 8)}...${apiKey.substring(apiKey.length - 8)}`
      : apiKey;
  };

  const generateUsername = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let username = '';
    for (let i = 0; i < 8; i++) {
      username += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({...formData, username: username});
  };

  const generatePassword = () => {
    const chars =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 16; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({...formData, password: password});
  };

  const getStatusColor = (status) => {
    return status === 'active' ? 'success' : 'default';
  };

  if (loading) {
    return (
      <Box>
        <Box
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          mb={3}
          sx={{
            background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
            color: 'white',
            padding: 2,
            borderRadius: 1,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}
        >
          <Typography variant='h4' component='h1' sx={{ color: 'white' }}>
            API Credentials Management
          </Typography>
          <Box>
            <Skeleton variant="rectangular" width={100} height={36} sx={{ mr: 2, bgcolor: 'rgba(255,255,255,0.2)' }} />
            <Skeleton variant="rectangular" width={180} height={36} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
          </Box>
        </Box>

        <Card>
          <CardContent>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>API Key</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Total Data</TableCell>
                    <TableCell>Valid Data</TableCell>
                    <TableCell>Incomplete Data</TableCell>
                    <TableCell>Dup - Free</TableCell>
                    <TableCell>Dup - Sent</TableCell>
                    <TableCell>Success Rate</TableCell>
                    <TableCell>Last Data</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...Array(5)].map((_, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Skeleton variant="text" width="80%" />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width="60%" />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="rectangular" width={60} height={24} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width="40%" />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width="40%" />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width="40%" />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width="40%" />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width="40%" />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="rectangular" width={60} height={8} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width="60%" />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="circular" width={24} height={24} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        mb={3}
        sx={{
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          color: 'white',
          padding: 2,
          borderRadius: 1,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
      >
        <Typography variant='h4' component='h1' sx={{ color: 'white' }}>
          API Credentials Management
        </Typography>
        <Box>
          <Button
            variant='outlined'
            startIcon={<RefreshIcon />}
            onClick={fetchCredentials}
            sx={{
              mr: 2,
              backgroundColor: 'rgba(255,255,255,0.1)',
              borderColor: 'rgba(255,255,255,0.3)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.2)',
                borderColor: 'rgba(255,255,255,0.5)',
              }
            }}
          >
            Refresh
          </Button>
          <Button
            variant='contained'
            startIcon={<AddIcon />}
            onClick={handleCreate}
            sx={{
              backgroundColor: 'rgba(255,255,255,0.9)',
              color: '#1976d2',
              '&:hover': {
                backgroundColor: 'white',
              }
            }}
          >
            Create New Credential
          </Button>
        </Box>
      </Box>


      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>API Key</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Total Data</TableCell>
                  <TableCell>Valid Data</TableCell>
                  <TableCell>Incomplete Data</TableCell>
                  <TableCell>Dup - Free</TableCell>
                  <TableCell>Dup - Sent</TableCell>
                  <TableCell>Success Rate</TableCell>
                  <TableCell>Last Data</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {credentials.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                      <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                        <InfoIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                        <Typography variant="h6" color="text.secondary">
                          No API Credentials Found
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Create your first API credential to get started
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={handleCreate}
                          sx={{ mt: 1 }}
                        >
                          Create New Credential
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  credentials.map((credential) => (
                  <TableRow key={credential.id}>
                    <TableCell>
                      <Box display='flex' alignItems='center' gap={1}>
                        <Typography variant='subtitle2'>
                          {credential.credential_name}
                        </Typography>
                        {credential.is_system_file_upload ? (
                          <Chip
                            label='System File Upload'
                            size='small'
                            color='primary'
                            variant='outlined'
                          />
                        ) : (
                          ''
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display='flex' alignItems='center'>
                        <Typography
                          variant='body2'
                          sx={{fontFamily: 'monospace'}}
                        >
                          {showApiKey[credential.id]
                            ? credential.api_key
                            : formatApiKey(credential.api_key)}
                        </Typography>
                        <IconButton
                          size='small'
                          onClick={() => toggleApiKeyVisibility(credential.id)}
                        >
                          {showApiKey[credential.id] ? (
                            <VisibilityOffIcon />
                          ) : (
                            <VisibilityIcon />
                          )}
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={credential.status}
                        color={getStatusColor(credential.status)}
                        size='small'
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant='body2' fontWeight='bold'>
                        {credential.totalData || 0}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display='flex' alignItems='center' gap={1}>
                        <CheckCircleIcon color='success' fontSize='small' />
                        <Typography variant='body2'>
                          {credential.validData || 0}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display='flex' alignItems='center' gap={1}>
                        <ErrorIcon color='error' fontSize='small' />
                        <Typography variant='body2'>
                          {credential.invalidData || 0}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display='flex' alignItems='center' gap={1}>
                        <Typography variant='body2' color='warning.main' fontWeight='bold'>
                          {credential.duplicatedFreeData || 0}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display='flex' alignItems='center' gap={1}>
                        <Typography variant='body2' color='error.main' fontWeight='bold'>
                          {credential.duplicatedSentData || 0}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display='flex' alignItems='center' gap={1}>
                        <LinearProgress
                          variant='determinate'
                          value={parseFloat(credential.successRate) || 0}
                          sx={{width: 60, height: 8, borderRadius: 4}}
                          color={
                            parseFloat(credential.successRate) >= 80
                              ? 'success'
                              : parseFloat(credential.successRate) >= 60
                              ? 'warning'
                              : 'error'
                          }
                        />
                        <Typography variant='body2' sx={{minWidth: 40}}>
                          {credential.successRate || 0}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant='body2'>
                        {credential.lastDataReceived
                          ? formatDateToJerusalem(credential.lastDataReceived, 'date-only')
                          : 'Never'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title='View Details'>
                        <IconButton
                          size='small'
                          onClick={() =>
                            navigate(`/api-credentials/${credential.id}`)
                          }
                          color='primary'
                        >
                          <InfoIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>
          {editingCredential
            ? 'Edit API Credential'
            : 'Create New API Credential'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{mt: 1}}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label='Credential Name'
                value={formData.credential_name}
                onChange={(e) =>
                  setFormData({...formData, credential_name: e.target.value})
                }
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label='Username *'
                value={formData.username}
                onChange={(e) =>
                  setFormData({...formData, username: e.target.value})
                }
                required
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={generateUsername} size='small'>
                      <RefreshIcon />
                    </IconButton>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label='Password *'
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) =>
                  setFormData({...formData, password: e.target.value})
                }
                required
                InputProps={{
                  endAdornment: (
                    <Box display='flex' gap={0.5}>
                      <IconButton onClick={generatePassword} size='small'>
                        <RefreshIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        size='small'
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Box>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.is_system_file_upload}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        is_system_file_upload: e.target.checked,
                      })
                    }
                  />
                }
                label='System File Upload Credential (Only one can be active)'
              />
              {formData.is_system_file_upload && (
                <Alert severity='info' sx={{mt: 1}}>
                  Setting this as system file upload will unset the flag on
                  other credentials.
                </Alert>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSave} variant='contained'>
            {editingCredential ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AllCredentials;
